'use client';

import type { AuthSnapshot } from '@lib/data/auth-snapshot';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type AuthContextValue = {
  readonly isLoggedIn: boolean;
  /** False until `rehydrate` has been called once. `isLoggedIn` defaults to
   * `false`, which is ambiguous between "confirmed logged out" and "not
   * checked yet" — consumers that would otherwise treat an unconfirmed
   * `false` as authoritative (e.g. `CartProvider`'s cart-changed handler)
   * need this to tell the two apart. */
  readonly resolved: boolean;
  /** `enable_newsletter_signup` flag, resolved alongside auth/cart by
   * `LayoutDataLoader`'s `getLayoutSnapshot()` call. Lives here (rather than
   * in `AuthSnapshot`) because it's independent of login state — `Footer`
   * reads it via `useAuth()` since that's the only bridge from
   * `LayoutDataLoader`'s fetch to sibling components today. */
  readonly showNewsletterSignup: boolean;
  readonly rehydrate: (snapshot: AuthSnapshot) => void;
  readonly setShowNewsletterSignup: (value: boolean) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export default function AuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [resolved, setResolved] = useState(false);
  const [showNewsletterSignup, setShowNewsletterSignup] = useState(false);

  const rehydrate = useCallback((snapshot: AuthSnapshot) => {
    setIsLoggedIn(snapshot.isLoggedIn);
    setResolved(true);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      isLoggedIn,
      resolved,
      showNewsletterSignup,
      rehydrate,
      setShowNewsletterSignup,
    }),
    [isLoggedIn, resolved, showNewsletterSignup, rehydrate],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    // Provider isn't mounted on a given route (e.g. checkout) — return a
    // benign logged-out default so consumers don't have to null-check.
    // `resolved: true` since there's no pending check to wait for here.
    return {
      isLoggedIn: false,
      resolved: true,
      showNewsletterSignup: false,
      rehydrate: () => undefined,
      setShowNewsletterSignup: () => undefined,
    };
  }
  return ctx;
}
